import React, { useState, useEffect, useCallback } from "react";
import { Container } from "react-bootstrap";
import { useNavigate } from "react-router-dom";
import { useTheme } from "../../contexts/ThemeContext";
import { useUser } from "../../contexts/UserContext";
import { StatCard } from "../../components/UI";
import { SearchAndFilterBar } from "../../components/search-and-filter-bar";
import ActiveFilters from "./components/ActiveFilters";
import UsersTable from "./components/UsersTable";
import ResponseModal from "../../components/modals/response-modal";
import FilesViewerModal from "../../components/modals/files-viewer/FilesViewerModal";
import EmptyState from "../../components/not-result";
import SEOHead from "../../components/SEO/SEOHead";
import userService from "../../services/userService";
import statsApi from "../../api/statsApi";
import AddForm from "../../components/modals/add-item";
import { getUserRole, canEditUsers, isAdminOrManager } from "../../utils/roleAccess";

const ITEMS_PER_PAGE = 10;

const normalizeUser = (rawUser = {}) => {
  const user = rawUser.data || rawUser;
  return {
    _id: user._id || user.id,
    id: user._id || user.id,
    firstName: user.firstName || "",
    lastName: user.lastName || "",
    email: user.email || "",
    companyName: user.companyName || "",
    phoneNumber: user.phoneNumber || "",
    profileImage: user.profileImage || null,
    pdfs: user.pdfs || [],
    role: user.role || "",
    status: user.status || "active",
    createdAt: user.createdAt ? new Date(user.createdAt) : new Date(),
    expanded: false,
    name: `${user.firstName || ""} ${user.lastName || ""}`.trim(),
  };
};

const UsersPage = () => {
  const { getThemeColors } = useTheme();
  const { user } = useUser();
  const colors = getThemeColors();
  const navigate = useNavigate();
  const role = getUserRole(user);
  const canEdit = canEditUsers(role);

  const [searchTerm, setSearchTerm] = useState("");
  const [activeFilters, setActiveFilters] = useState([]);
  const [selectedUsers, setSelectedUsers] = useState([]);
  const [filterValues, setFilterValues] = useState({});
  const [currentPage, setCurrentPage] = useState(1);
  const [users, setUsers] = useState([]);
  const [totalItems, setTotalItems] = useState(0);
  const [loading, setLoading] = useState(false);
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [editUserData, setEditUserData] = useState(null);
  const [statCards, setStatCards] = useState([]);
  const [statsLoading, setStatsLoading] = useState(false);
  const [fetchError, setFetchError] = useState(null);

  // Response modal state
  const [responseModal, setResponseModal] = useState({
    isOpen: false,
    message: '',
    status: 'success',
    title: ''
  });

  // Files viewer modal state
  const [filesModal, setFilesModal] = useState({
    isOpen: false,
    files: [],
    entityName: ''
  });

  const handleViewFiles = (user) => {
    setFilesModal({
      isOpen: true,
      files: user.pdfs || [],
      entityName: user.name || `${user.firstName} ${user.lastName}`.trim()
    });
  };

  const closeFilesModal = () => {
    setFilesModal({ isOpen: false, files: [], entityName: '' });
  };

  const showResponseModal = (message, status = 'success', title = '') => {
    setResponseModal({
      isOpen: true,
      message,
      status,
      title
    });
  };

  const closeResponseModal = () => {
    setResponseModal(prev => ({ ...prev, isOpen: false }));
  };

  // Fetch statistics for stat cards
  const fetchStats = useCallback(async () => {
    if (!isAdminOrManager(role)) {
      setStatCards([]);
      return;
    }
    setStatsLoading(true);
    try {
      const now = new Date();
      const year = now.getFullYear();
      const from = `${year}-01-01`;
      const to = `${year}-12-31`;
      const response = await statsApi.getStats({ period: 'range', from, to, scopeType: 'global', grain: 'month' });
        const apiResponse = response?.data || response;
        const stats = apiResponse?.data || apiResponse;
        const totals = stats.totals || {};

        if (!stats) {
          setStatCards([]);
          return;
        }

        const monthKey = `${year}-${String(now.getMonth() + 1).padStart(2, '0')}`;
        const normalizeKey = (key) => (typeof key === 'string' ? key.slice(0, 7) : '');
        const points = Array.isArray(stats.points) ? stats.points : [];
        const pastAndCurrentPoints = points.filter((p) => normalizeKey(p?.periodKey) <= monthKey);
        const lastPoints = pastAndCurrentPoints.slice(-5);
        const loadsSeries = lastPoints.map((p) => Number(p?.totals?.totalLoads || 0));
        const revenueSeries = lastPoints.map((p) => Number(p?.totals?.totalRevenue || 0));
        const profitSeries = lastPoints.map((p) => Number(p?.totals?.totalProfit || 0));

        const totalLoadsTaken = totals.totalLoads || 0;
        const totalRevenue = totals.totalRevenue || 0;
        const totalProfit = totals.totalProfit || 0;

        setStatCards([
          {
            title: 'Total Loads Taken',
            value: String(totalLoadsTaken),
            diagramType: 'bar',
            diagramData: { data: loadsSeries, width: 125, height: 60 }
          },
          {
            title: 'Total Revenue',
            value: `$${Number(totalRevenue).toLocaleString()}`,
            variant: 'blue',
            diagramType: 'area',
            diagramData: { data: revenueSeries, width: 125, height: 60 }
          },
          {
            title: 'Total Profit',
            value: `$${Number(totalProfit).toLocaleString()}`,
            diagramType: 'bar',
            diagramData: { data: profitSeries, width: 125, height: 60 }
          }
        ]);
    } catch (err) {
      console.error("Error fetching users stats:", err);
      setStatCards([]);
    } finally {
      setStatsLoading(false);
    }
  }, [role]);

  useEffect(() => {
    fetchStats();
  }, [fetchStats]);

  const buildFilterParams = (values = {}) => {
    const params = {};
    const setIf = (key, value) => {
      if (value === undefined || value === null) return;
      if (Array.isArray(value) && value.length === 0) return;
      if (typeof value === 'string' && value.trim() === '') return;
      params[key] = value;
    };

    setIf('firstName', values.firstName);
    setIf('lastName', values.lastName);
    setIf('email', values.email);
    setIf('companyName', values.companyName);
    setIf('status', values.status);
    setIf('role', values.role);

    if (values.createdDate) {
      params.createdAt = values.createdDate;
    }

    return params;
  };

  const fetchUsers = useCallback(async () => {
    setLoading(true);
    setFetchError(null);

    try {
      const params = {
        page: currentPage,
        limit: ITEMS_PER_PAGE,
        sortBy: "createdAt",
        sortOrder: "desc",
      };

      if (searchTerm.trim()) {
        params.search = searchTerm.trim();
      }

      const filterParams = buildFilterParams(filterValues);
      const hasFilters = Object.keys(filterParams).length > 0;
      Object.assign(params, filterParams);

      let response;
      if (searchTerm.trim() || hasFilters) {
        response = await userService.searchUsers(searchTerm.trim(), params);
      } else {
        response = await userService.getUsers(params);
      }

      const apiResponse = response?.data || response;
      const rawUsers = Array.isArray(apiResponse?.data) ? apiResponse.data : [];
      const pagination = apiResponse?.pagination || apiResponse?.meta || {};

      if (!Array.isArray(rawUsers) || rawUsers.length === 0) {
        console.warn("No users found in API response");
        setUsers([]);
        setTotalItems(0);
        return;
      }

      const normalizedUsers = rawUsers.map(normalizeUser);
      const total = pagination.total ?? pagination.totalItems ?? rawUsers.length;
      const totalPages =
        pagination.totalPages ??
        (total > 0 ? Math.ceil(total / ITEMS_PER_PAGE) : 1);

      if (totalPages > 0 && currentPage > totalPages) {
        setCurrentPage(totalPages);
        return;
      }

      setUsers(normalizedUsers);
      setTotalItems(total);
    } catch (err) {
      console.error("Error fetching users:", err);
      setFetchError(err?.response?.data?.error || err?.message || "Failed to fetch users");
      setUsers([]);
      setTotalItems(0);
    } finally {
      setLoading(false);
    }
  }, [currentPage, searchTerm, filterValues]);

  useEffect(() => {
    fetchUsers();
  }, [fetchUsers]);

  const handleFilterApply = (filters) => {
    setFilterValues(filters);
    
    // Convert filter object to array of filter strings for ActiveFilters display
    const filterArray = [];
    Object.keys(filters).forEach(key => {
      const value = filters[key];
      if (value !== '' && value !== null && value !== undefined) {
        if (Array.isArray(value) && value.length > 0) {
          filterArray.push(`${key}: ${value.join(', ')}`);
        } else if (typeof value === 'boolean' && value) {
          filterArray.push(key);
        } else if (typeof value === 'string' && value.trim() !== '') {
          filterArray.push(`${key}: ${value}`);
        }
      }
    });
    setActiveFilters(filterArray);
    setCurrentPage(1);
  };

  const handleRemoveFilter = (filterIndex) => {
    const filterToRemove = activeFilters[filterIndex];
    const newFilters = activeFilters.filter(
      (_, index) => index !== filterIndex
    );
    setActiveFilters(newFilters);
    
    // Also update filterValues
    const updatedFilterValues = { ...filterValues };
    const key = filterToRemove.split(':')[0].trim();
    if (updatedFilterValues[key] !== undefined) {
      if (Array.isArray(updatedFilterValues[key])) {
        updatedFilterValues[key] = [];
      } else {
        updatedFilterValues[key] = '';
      }
      setFilterValues(updatedFilterValues);
    }
    setCurrentPage(1);
  };

  const handleClearAllFilters = () => {
    setActiveFilters([]);
    setFilterValues({});
    setCurrentPage(1);
  };

  const handleSelectAll = (pageItemIds = []) => {
    if (pageItemIds.length === 0) {
      setSelectedUsers([]);
      return;
    }

    const hasSelectedAll = pageItemIds.every((id) =>
      selectedUsers.includes(id)
    );

    if (hasSelectedAll) {
      setSelectedUsers((prev) => prev.filter((id) => !pageItemIds.includes(id)));
    } else {
      setSelectedUsers((prev) => {
        const merged = new Set(prev);
        pageItemIds.forEach((id) => merged.add(id));
        return Array.from(merged);
      });
    }
  };

  const handleSelectUser = (userId) => {
    setSelectedUsers((prev) =>
      prev.includes(userId)
        ? prev.filter((id) => id !== userId)
        : [...prev, userId]
    );
  };

  const handleDeleteSelected = async (idsToDelete) => {
    if (!idsToDelete || idsToDelete.length === 0) {
      if (selectedUsers.length === 0) return;
      idsToDelete = selectedUsers;
    }

    try {
      setLoading(true);
      await Promise.all(
        idsToDelete.map((id) => userService.deleteUser(id))
      );
      
      showResponseModal(
        `Successfully deleted ${idsToDelete.length} user(s)`,
        'success',
        'Deleted'
      );
      await fetchUsers();
      setSelectedUsers([]);
    } catch (err) {
      console.error("Error deleting users:", err);
      showResponseModal(
        err?.response?.data?.error || err?.message || "Failed to delete users",
        'error',
        'Delete Failed'
      );
    } finally {
      setLoading(false);
    }
  };

  const handleToggleExpand = (userId) => {
    setUsers((prev) =>
      prev.map((user) =>
        (user._id || user.id) === userId
          ? { ...user, expanded: !user.expanded }
          : user
      )
    );
  };


  return (
    <>
      <SEOHead
        title="User Management"
        description="Manage user accounts, roles, and permissions. Control access to your logistics management system with comprehensive user administration tools."
        keywords="user management, user administration, access control, user roles, logistics users, system users, user accounts"
        url="/users"
      />
      <Container
        fluid
        style={{ backgroundColor: colors.background, minHeight: "100vh" }}
      >
      {statCards.length > 0 && (
        <div className="stat-cards">
          {statCards.map((card, index) => (
            <div key={index} className="stat-cards__wrapper">
              <StatCard {...card} />
            </div>
          ))}
        </div>
      )}
      
      <SearchAndFilterBar
        searchTerm={searchTerm}
        onSearchChange={setSearchTerm}
        onFilterApply={handleFilterApply}
        pageType="users"
        initialFilters={filterValues}
        showAddButton={canEdit}
        onAddSuccess={() => {
          showResponseModal('User created successfully', 'success', 'Success');
          fetchUsers();
        }}
      />

      {activeFilters.length > 0 && (
        <ActiveFilters
          filters={activeFilters}
          onRemoveFilter={handleRemoveFilter}
          onClearAll={handleClearAllFilters}
        />
      )}

      {fetchError ? (
        <EmptyState
          variant="error"
          title="Failed to load data"
          message={fetchError}
          onRetry={fetchUsers}
          retryText="Retry"
        />
      ) : users.length === 0 && !loading ? (
        <EmptyState
          variant={searchTerm ? "search" : "empty"}
          title={searchTerm ? "No results found" : "No users yet"}
          message={searchTerm ? `No users matching "${searchTerm}"` : "Add your first user to get started"}
        />
      ) : (
        <UsersTable
          users={users}
          selectedUsers={selectedUsers}
          onSelectAll={handleSelectAll}
          onSelectUser={handleSelectUser}
          onToggleExpand={handleToggleExpand}
          onDeleteSelected={handleDeleteSelected}
          onEdit={(userRow) => {
            if (!canEdit) return;
            setEditUserData(userRow);
            setIsEditModalOpen(true);
          }}
          onViewFiles={handleViewFiles}
          canSelect={canEdit}
          canDelete={canEdit}
          canEdit={canEdit}
          canChangeStatus={canEdit}
          onChangeStatus={async (id, status) => {
            try {
              await userService.updateUserStatus(id, status);
              showResponseModal(
                `User status updated to ${status}`,
                'success',
                'Status Updated'
              );
              await fetchUsers();
            } catch (error) {
              showResponseModal(
                error?.response?.data?.error || error?.message || `Failed to update user status`,
                'error',
                'Update Failed'
              );
            }
          }}
          currentPage={currentPage}
          onPageChange={setCurrentPage}
          loading={loading}
          totalItems={totalItems}
          itemsPerPage={ITEMS_PER_PAGE}
        />
      )}


      {isEditModalOpen && editUserData && (
        <AddForm
          isOpen={isEditModalOpen}
          setIsOpen={setIsEditModalOpen}
          formType="users"
          userData={{
            fullName: `${editUserData.firstName || ''} ${editUserData.lastName || ''}`.trim() || editUserData.name,
            email: editUserData.email,
            companyName: editUserData.companyName,
            phoneNumber: editUserData.phoneNumber || "",
            role: editUserData.role,
            avatarUrl: editUserData.profileImage,
            active: editUserData.status === 'active',
            message: editUserData.message || "",
            allowedCustomers: editUserData.allowedCustomers || [],
            pdfs: editUserData.pdfs || []
          }}
          isEditMode={true}
          itemId={editUserData._id || editUserData.id}
          onSuccess={() => {
            showResponseModal('User updated successfully', 'success', 'Success');
            fetchUsers();
            setIsEditModalOpen(false);
            setEditUserData(null);
          }}
          onClose={() => {
            setIsEditModalOpen(false);
            setEditUserData(null);
          }}
        />
      )}

      <ResponseModal
        isOpen={responseModal.isOpen}
        message={responseModal.message}
        status={responseModal.status}
        title={responseModal.title}
        onClose={closeResponseModal}
      />

      <FilesViewerModal
        isOpen={filesModal.isOpen}
        onClose={closeFilesModal}
        files={filesModal.files}
        title="User Documents"
        entityName={filesModal.entityName}
      />
    </Container>
    </>
  );
};

export default UsersPage;
